import { useCallback, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  getDataMapFromCommonResponse,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import { getSPDataWithParams } from 'src/supportPortal/services/services'

/**
 * Generic hook for fetching CommonResponse format data
 *
 * @param {Array} endpoints Array of endpoint configurations
 * @param {string} endpoints[].url - API endpoint URL
 * @param {Object} endpoints[].params - API parameters
 * @param {string} endpoints[].dataKey - Key to extract from CommonResponse
 * @param {string} endpoints[].errorMessage - Error message on failure
 * @param {string} endpoints[].stateKey - Key to store data in state
 *
 * @returns {Object} Hook state and methods
 */
export const useCommonResponseData = (endpoints = []) => {
  const [data, setData] = useState({})
  const [loading, setLoading] = useState(false)

  // Stabilize endpoints array to prevent unnecessary re-renders
  const endpointsString = JSON.stringify(endpoints)
  const stableEndpoints = useMemo(() => endpoints, [endpointsString])

  // Generic fetch function for CommonResponse APIs
  const fetchCommonResponseData = useCallback(async (url, params, dataKey, errorMessage) => {
    try {
      const resp = await getSPDataWithParams(url, params)
      if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
        return getDataMapFromCommonResponse(resp)?.[dataKey] || []
      }
      throw new Error(resp?.data?.message || errorMessage)
    } catch (error) {
      console.error(`Error fetching ${dataKey}:`, error)
      toast.error(errorMessage)
      return []
    }
  }, [])

  // Fetch single endpoint
  const fetchEndpoint = useCallback(
    async (endpoint) => {
      const result = await fetchCommonResponseData(
        endpoint.url,
        endpoint.params || {},
        endpoint.dataKey,
        endpoint.errorMessage
      )
      setData((prev) => ({
        ...prev,
        [endpoint.stateKey]: result,
      }))
      return result
    },
    [fetchCommonResponseData]
  )

  // Fetch all configured endpoints
  const fetchAllEndpoints = useCallback(async () => {
    setLoading(true)
    try {
      const results = await Promise.all(stableEndpoints.map(fetchEndpoint))
      return results
    } finally {
      setLoading(false)
    }
  }, [stableEndpoints, fetchEndpoint])

  // Fetch specific endpoint by key
  const fetchByKey = useCallback(
    async (stateKey) => {
      const endpoint = stableEndpoints.find((ep) => ep.stateKey === stateKey)
      if (endpoint) {
        return await fetchEndpoint(endpoint)
      }
      console.warn(`Endpoint with stateKey '${stateKey}' not found`)
      return []
    },
    [stableEndpoints, fetchEndpoint]
  )

  return {
    data,
    loading,
    fetchAllEndpoints,
    fetchByKey,
    fetchCommonResponseData,
  }
}